// Steps/ReceptionistConfigStep.jsx
// Configures a NEW AI receptionist (name / system prompt / greeting / voice /
// model) and creates it via thirdPartyVapiCreate, capturing the returned
// New_VirtualReceptionist_Id into wizard.receptionistId. Advances on success.

import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Label, Input, FormGroup, Spinner } from "reactstrap";
import { useSelector } from "react-redux";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";

import {
  thirdPartyVapiCreate,
  thirdPartyVapiCreateSuccess,
} from "../../../../store/thirdPartyIntegration/action";
import {
  VOICE_OPTIONS,
  MODEL_OPTIONS,
  SAMPLE_PROMPTS,
} from "../../../../pages/Settings/company/thirdPartyIntegration/receptionistOptions";

export function ReceptionistConfigStep({ wizard }) {
  const {
    dispatch,
    entryMode,
    name,
    setName,
    systemPrompt,
    setSystemPrompt,
    greeting,
    setGreeting,
    voice,
    setVoice,
    model,
    setModel,
    setReceptionistId,
    receptionistSaved,
    setReceptionistSaved,
    goNext,
    goBack,
  } = wizard;

  const { createDataAPI, createLoading } = useSelector((state) => ({
    createDataAPI: state.ThirdPartyIntegration.thirdPartyVapiCreateData,
    createLoading: state.ThirdPartyIntegration.thirdPartyVapiCreateLoading,
  }));

  useEffect(() => {
    if (isEmpty(createDataAPI)) return;
    if (createDataAPI.New_VirtualReceptionist_Id || createDataAPI.status === 200) {
      toast.success(createDataAPI?.message || "Receptionist created");
      if (createDataAPI.New_VirtualReceptionist_Id) {
        setReceptionistId(createDataAPI.New_VirtualReceptionist_Id);
      }
      setReceptionistSaved(true);
      dispatch(thirdPartyVapiCreateSuccess([]));
      goNext();
    } else {
      toast.error(createDataAPI?.error || createDataAPI?.message);
      dispatch(thirdPartyVapiCreateSuccess([]));
    }
  }, [createDataAPI]);

  const handleSave = () => {
    if (!name.trim()) {
      toast.error("Give the receptionist a name");
      return;
    }
    if (!systemPrompt.trim()) {
      toast.error("Enter the AI answering prompt");
      return;
    }
    dispatch(
      thirdPartyVapiCreate([
        {
          name: name.trim(),
          system_prompt: systemPrompt,
          greeting,
          voice,
          model,
          enabled: 1,
        },
      ])
    );
  };

  return (
    <div className="step-container">
      <div className="mb-4 pb-2 border-bottom">
        <h4 className="text-pastel-blue fw-bold mb-1">
          Set up your AI receptionist
        </h4>
        <p className="text-muted small mb-0">
          Tell the AI how to greet and handle inbound callers.
        </p>
      </div>

      <FormGroup>
        <Label for="rc-name">Name</Label>
        <Input
          id="rc-name"
          type="text"
          value={name}
          placeholder="e.g. Main line, Out of hours"
          onChange={(e) => setName(e.target.value)}
        />
      </FormGroup>

      <FormGroup>
        <div className="d-flex justify-content-between align-items-center">
          <Label for="rc-system-prompt" className="mb-0">
            AI Answering Prompt
          </Label>
          <Input
            type="select"
            bsSize="sm"
            style={{ width: "auto" }}
            value=""
            onChange={(e) => {
              const s = SAMPLE_PROMPTS[Number(e.target.value)];
              if (!s) return;
              setSystemPrompt(s.prompt);
              if (s.greeting) setGreeting(s.greeting);
            }}
          >
            <option value="">Insert a sample prompt…</option>
            {SAMPLE_PROMPTS.map((s, i) => (
              <option key={s.label} value={i}>
                {s.label}
              </option>
            ))}
          </Input>
        </div>
        <Input
          id="rc-system-prompt"
          type="textarea"
          rows={7}
          className="mt-1"
          value={systemPrompt}
          placeholder="You are the friendly virtual receptionist for..."
          onChange={(e) => setSystemPrompt(e.target.value)}
        />
        <small className="text-muted">
          Pick a sample to start, then tweak it
        </small>
      </FormGroup>

      <FormGroup>
        <Label for="rc-greeting">Greeting</Label>
        <Input
          id="rc-greeting"
          type="text"
          value={greeting}
          placeholder="Hello, thanks for calling - how can I help?"
          onChange={(e) => setGreeting(e.target.value)}
        />
        <small className="text-muted">The first thing the AI says</small>
      </FormGroup>

      <div className="row">
        <div className="col-md-6">
          <FormGroup>
            <Label for="rc-voice">Voice</Label>
            <Input
              id="rc-voice"
              type="select"
              value={voice || ""}
              onChange={(e) => setVoice(e.target.value)}
            >
              <option value="">Default voice</option>
              {VOICE_OPTIONS.map((v) => (
                <option key={v.value} value={v.value}>
                  {v.label}
                </option>
              ))}
            </Input>
          </FormGroup>
        </div>
        <div className="col-md-6">
          <FormGroup>
            <Label for="rc-model">Model</Label>
            <Input
              id="rc-model"
              type="select"
              value={model || ""}
              onChange={(e) => setModel(e.target.value)}
            >
              <option value="">Default model</option>
              {MODEL_OPTIONS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </Input>
          </FormGroup>
        </div>
      </div>

      <div className="d-flex align-items-center justify-content-between mt-4 pt-3 border-top">
        {/* Receptionist mode starts here — no Back on the first step. */}
        {entryMode === "receptionist" ? (
          <span />
        ) : (
          <button
            type="button"
            className="btn btn-light rounded-pill px-4 fw-semibold text-muted"
            onClick={goBack}
            disabled={createLoading}
          >
            <i className="ri-arrow-left-line me-2"></i> Back
          </button>
        )}

        {receptionistSaved ? (
          <button
            type="button"
            className="btn btn-primary rounded-pill px-4 fw-semibold"
            onClick={goNext}
          >
            Next <i className="ri-arrow-right-line ms-2"></i>
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-success rounded-pill px-4 fw-semibold"
            onClick={handleSave}
            disabled={createLoading}
          >
            {createLoading ? (
              <>
                <Spinner size="sm" className="me-2" /> Creating…
              </>
            ) : (
              <>
                <i className="ri-save-line me-2"></i> Create
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

ReceptionistConfigStep.propTypes = {
  wizard: PropTypes.object.isRequired,
};
